import { Injectable } from '@angular/core'
import { UntypedFormGroup, UntypedFormArray, AbstractControl } from '@angular/forms'
import { FieldTree } from '@angular/forms/signals'

import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'

export interface FieldErrors {
  [field: string]: string | string[]
}

// Signal Formsin virhelajit, joiden käännösavain on reaktiivisten lomakkeiden aikaisessa muodossa
const KAANNOSAVAINTEN_VIRHELAJIT: Record<string, string> = { minLength: 'minlength', maxLength: 'maxlength' }

@Injectable()
export class FormValidationService {
  constructor(private translation: LemonTranslationService) {}

  private getErrorLocalizationKey(view: string, field: string, validator: string): string {
    return view + '.validation.' + field + '.' + validator
  }

  naytaEnsimmainenVirhe() {
    const firstInvalidField = document.querySelector('.mat-form-field-invalid') as HTMLElement
    if (firstInvalidField) {
      // console.log('löytyi', firstInvalidField)
      if ((firstInvalidField as any).scrollIntoView) {
        ;(firstInvalidField as any).scrollIntoView({ behavior: 'smooth' })
      } else if ((firstInvalidField as any).scrollIntoViewIfNeeded) {
        ;(firstInvalidField as any).scrollIntoViewIfNeeded()
      }
    }
  }

  onkoLomakkeessaVirheita<T>(form: UntypedFormGroup | FieldTree<T>): boolean {
    if (!form) {
      return false
    }
    if (!(form instanceof AbstractControl)) {
      return form().invalid()
    }

    const avaimet = Object.keys(form.controls)
    for (const fld of avaimet) {
      const ctrl = form.controls[fld]
      if (this.onkoVirheita(ctrl)) {
        return true
      }
    }
    return false
  }

  onkoVirheita(control: AbstractControl): boolean {
    if (control && !control.valid && !control.disabled) {
      return true
    } else if (control instanceof UntypedFormArray) {
      for (const ctrl of control.controls) {
        if (this.onkoVirheita(ctrl)) {
          return true
        }
      }
    } else if (control instanceof UntypedFormGroup) {
      const avaimet = Object.keys(control.controls)
      for (const fld of avaimet) {
        const ctrl = control.controls[fld]
        if (this.onkoVirheita(ctrl)) {
          return true
        }
      }
    }
    return false
  }

  tarkistaVirheetLapsistaJaItsesta(control: AbstractControl) {
    if (control instanceof UntypedFormArray) {
      for (const ctrl of control.controls) {
        this.tarkistaVirheetLapsistaJaItsesta(ctrl)
      }
    } else if (control instanceof UntypedFormGroup) {
      const avaimet = Object.keys(control.controls)
      for (const fld of avaimet) {
        const ctrl = control.controls[fld]
        this.tarkistaVirheetLapsistaJaItsesta(ctrl)
      }
    }
    control.updateValueAndValidity({ emitEvent: false })
  }

  merkitseKokoLomakeKosketuksi<T>(form: UntypedFormGroup | FieldTree<T>): void {
    if (!form) {
      return
    }
    if (form instanceof AbstractControl) {
      form.markAllAsTouched()
    } else {
      form().markAsTouched()
    }
    setTimeout(() => {
      this.naytaEnsimmainenVirhe()
    }, 100)
  }

  updateValidationStatus<T>(view: string, form: UntypedFormGroup | FieldTree<T>, produceErrorsAlways?: boolean): FieldErrors {
    if (!form) {
      return
    }
    const fldErrors: FieldErrors = {}
    if (!(form instanceof AbstractControl)) {
      for (const [field, kentta] of form as unknown as Iterable<[string, FieldTree<unknown>]>) {
        this._paivitaKentanVirheet(kentta, view, field, fldErrors, -1, !!produceErrorsAlways)
      }
      return fldErrors
    }
    Object.keys(form.controls).forEach(field => {
      const control = form.controls[field]
      this.updateValidationStatusForControl(control, view, field, fldErrors, -1, produceErrorsAlways ? true : false)
    })
    return fldErrors
  }

  private updateValidationStatusForControl(control: AbstractControl, view: string, field: string, fldErrors: FieldErrors, index: number, produceErrorsAlways: boolean) {
    if (control instanceof UntypedFormArray) {
      let i = 0
      for (const ctrl of control.controls) {
        this.updateValidationStatusForControl(ctrl, view, field, fldErrors, i, produceErrorsAlways)
        i++
      }
    } else if (control instanceof UntypedFormGroup) {
      Object.keys(control.controls).forEach(fld => {
        const ctrl = control.controls[fld]
        this.updateValidationStatusForControl(ctrl, view, fld, fldErrors, index, produceErrorsAlways)
      })
    } else {
      // if (!control.valid) {
      //   console.log('NOT VALID:', field)
      // }
      for (const key in control.errors) {
        if (control && !control.valid && (control.touched || produceErrorsAlways)) {
          this._lisaaVirheviesti(fldErrors, view, field, key, index)
        }
      }
    }
  }

  private _paivitaKentanVirheet(kentta: FieldTree<unknown>, view: string, field: string, fldErrors: FieldErrors, index: number, produceErrorsAlways: boolean) {
    const tila = kentta()
    const arvo = tila.value()
    if (Array.isArray(arvo)) {
      let i = 0
      for (const alkio of kentta as unknown as Iterable<FieldTree<unknown>>) {
        this._paivitaKentanVirheet(alkio, view, field, fldErrors, i, produceErrorsAlways)
        i++
      }
      return
    }
    if (tila.invalid() && (tila.touched() || produceErrorsAlways)) {
      for (const virhe of tila.errors()) {
        this._lisaaVirheviesti(fldErrors, view, field, KAANNOSAVAINTEN_VIRHELAJIT[virhe.kind] ?? virhe.kind, index)
      }
    }
    if (arvo !== null && typeof arvo === 'object' && !(arvo instanceof Date)) {
      for (const [nimi, lapsi] of kentta as unknown as Iterable<[string, FieldTree<unknown>]>) {
        this._paivitaKentanVirheet(lapsi, view, nimi, fldErrors, index, produceErrorsAlways)
      }
    }
  }

  private _lisaaVirheviesti(fldErrors: FieldErrors, view: string, field: string, key: string, index: number) {
    const kokoAvain = this.getErrorLocalizationKey(view, field, key)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const viesti = this.translation.lokalisoi(kokoAvain, { rivi_nro: index + 1 })
    if (index > -1) {
      if (fldErrors[field]) {
        const arr = fldErrors[field] as string[]
        if (arr[index]) {
          arr[index] += ' ' + viesti
        }
      } else {
        const arr: string[] = []
        arr[index] = viesti
        fldErrors[field] = arr
      }
    } else {
      if (fldErrors[field]) {
        fldErrors[field] += ' ' + viesti
      } else {
        fldErrors[field] = viesti
      }
    }
  }

  public processValue(value: any) {
    if (value !== undefined && value !== null) {
      if (typeof value === 'string') {
        return value.replace(/\s+/g, ' ').trim()
      }

      return value
    }
    return null
  }

  public sanitizeWhitespace(value: any) {
    if (value !== undefined && value !== null) {
      if (typeof value === 'string') {
        return value.replace(/[ \t]+/g, ' ').trim()
      }

      return value
    }
    return null
  }
}
